import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Search, Filter, Loader2, ArrowLeft } from 'lucide-react';
import * as agentService from '../../../services/agentService';
import { Agent, Tag } from '../../../types/agent';
import { PaginationMetadata } from '../../../types/user';
import { AgentCard } from '../ui/AgentCard';
import { AgentDetailModal } from '../ui/AgentDetailModal';
import { VisibilityPills, VisibilityFilter } from '../ui/VisibilityPills';
import { STORAGE_KEYS } from '../../chat/constants';

const PAGE_SIZE = 12;

/**
 * AgentCatalogPage - Catálogo de agentes em página cheia (rota /agentes).
 * Substitui o antigo AgentSelectorModal nos fluxos de chat e projetos.
 */
export const AgentCatalogPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo: string = location.state?.returnTo || '/chat';

  const [agents, setAgents] = useState<Agent[]>([]);
  const [metadata, setMetadata] = useState<PaginationMetadata | null>(null);
  const [tags, setTags] = useState<Tag[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [selectedTagIds, setSelectedTagIds] = useState<number[]>([]);
  const [visibilityFilter, setVisibilityFilter] = useState<VisibilityFilter>('all');
  const [showTagFilter, setShowTagFilter] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [detailAgent, setDetailAgent] = useState<Agent | null>(null);
  const [currentSkip, setCurrentSkip] = useState(0);

  const requestIdRef = useRef(0);

  const activeAgentId = Number(localStorage.getItem(STORAGE_KEYS.lastUsedAgentId)) || null;

  // Debounce da busca
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // ESC volta para a origem
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (detailAgent) {
          setDetailAgent(null);
          return;
        }
        navigate(returnTo);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navigate, returnTo, detailAgent]);

  // Tags (uma vez)
  useEffect(() => {
    agentService.getAllTags().then(setTags).catch((err) => {
      console.error('Error loading tags:', err);
    });
  }, []);

  // Busca/filtros (com guarda de corrida)
  const fetchAgents = useCallback(async (skip: number, append: boolean) => {
    const requestId = ++requestIdRef.current;
    if (!append) setIsLoading(true); else setIsLoadingMore(true);
    setError(null);
    try {
      const response = await agentService.getAvailableAgents(
        skip,
        PAGE_SIZE,
        debouncedQuery || undefined,
        selectedTagIds.length > 0 ? selectedTagIds : undefined,
        visibilityFilter !== 'all' ? visibilityFilter : undefined,
        'most_used'
      );
      if (requestId !== requestIdRef.current) return;
      if (response?.success) {
        setAgents((prev) => (append ? [...prev, ...response.data] : response.data));
        setMetadata(response.metadata);
        setCurrentSkip(skip);
      } else {
        setError('Falha ao buscar agentes');
      }
    } catch (err) {
      if (requestId !== requestIdRef.current) return;
      setError(err instanceof Error ? err.message : 'Erro ao buscar agentes');
    } finally {
      if (requestId === requestIdRef.current) {
        setIsLoading(false);
        setIsLoadingMore(false);
      }
    }
  }, [debouncedQuery, selectedTagIds, visibilityFilter]);

  useEffect(() => {
    fetchAgents(0, false);
  }, [fetchAgents]);

  // Seleção: persiste e volta para a origem com o agente no state
  const handleSelect = (agent: Agent) => {
    localStorage.setItem(STORAGE_KEYS.lastUsedAgentId, agent.id.toString());
    navigate(returnTo, { state: { selectedAgent: agent } });
  };

  // Pin otimista com rollback
  const handleTogglePin = async (agent: Agent) => {
    const nextPinned = !agent.pinned;
    setAgents((prev) => prev.map((a) => (a.id === agent.id ? { ...a, pinned: nextPinned } : a)));
    try {
      if (nextPinned) {
        await agentService.pinAgent(agent.id);
      } else {
        await agentService.unpinAgent(agent.id);
      }
    } catch (err) {
      console.error('Error toggling pin:', err);
      setAgents((prev) => prev.map((a) => (a.id === agent.id ? { ...a, pinned: !nextPinned } : a)));
    }
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setSelectedTagIds([]);
    setVisibilityFilter('all');
  };

  const hasFilters = Boolean(debouncedQuery) || selectedTagIds.length > 0 || visibilityFilter !== 'all';

  return (
    <div className="min-h-screen bg-[#242424] flex flex-col">
      <div className="flex-1 w-full max-w-5xl mx-auto px-4 sm:px-8 pt-8 pb-12 flex flex-col">
        {/* Voltar */}
        <button
          onClick={() => navigate(returnTo)}
          className="flex items-center gap-2 text-[#F4CC04] text-sm font-medium hover:text-[#d4b104] transition-colors self-start mb-4"
        >
          <ArrowLeft size={16} />
          Voltar
        </button>

        {/* Header */}
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 mb-4">
          <h1 className="text-white text-2xl font-bold">Agentes</h1>
          {metadata && (
            <span className="text-[#666666] text-sm">
              {metadata.total} disponíve{metadata.total !== 1 ? 'is' : 'l'} · ordenados por mais usados
            </span>
          )}
        </div>

        {/* Pills de visibilidade */}
        <div className="mb-3">
          <VisibilityPills value={visibilityFilter} onChange={setVisibilityFilter} />
        </div>

        {/* Busca + filtros */}
        <div className="flex items-center gap-2 mb-3">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#666666]" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Buscar por nome ou descrição..."
              autoFocus
              className="
                w-full pl-10 pr-4 py-2.5
                bg-[#1a1a1a] border border-[#333333] rounded-lg
                text-white text-sm placeholder:text-[#666666]
                focus:outline-none focus:border-[#F4CC04]
              "
            />
          </div>
          <button
            onClick={() => setShowTagFilter(!showTagFilter)}
            className={`
              flex items-center gap-2 px-3 py-2.5 border rounded-lg text-sm transition-colors flex-shrink-0
              ${selectedTagIds.length > 0
                ? 'border-[#F4CC04] text-[#F4CC04]'
                : 'border-[#333333] text-[#666666] hover:text-white'
              }
            `}
          >
            <Filter className="w-4 h-4" />
            <span className="hidden sm:inline">Filtros</span>
            {selectedTagIds.length > 0 && (
              <span className="bg-[#F4CC04] text-[#1a1a1a] text-xs px-1.5 py-0.5 rounded-full">
                {selectedTagIds.length}
              </span>
            )}
          </button>
        </div>

        {/* Painel de tags (comportamento atual mantido) */}
        {showTagFilter && (
          <div className="bg-[#1a1a1a] border border-[#333333] rounded-lg p-3 mb-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[#666666] text-xs">Filtrar por tags</span>
              {selectedTagIds.length > 0 && (
                <button onClick={() => setSelectedTagIds([])} className="text-[#F4CC04] text-xs hover:underline">
                  Limpar
                </button>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {tags.map((tag) => (
                <button
                  key={tag.id}
                  onClick={() =>
                    setSelectedTagIds((prev) =>
                      prev.includes(tag.id) ? prev.filter((id) => id !== tag.id) : [...prev, tag.id]
                    )
                  }
                  className={`
                    px-2 py-1 text-xs rounded-full transition-colors
                    ${selectedTagIds.includes(tag.id)
                      ? 'bg-[#F4CC04] text-[#1a1a1a]'
                      : 'bg-[#333333] text-[#CCCCCC] hover:bg-[#444444]'
                    }
                  `}
                >
                  {tag.name}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Erro */}
        {error && !isLoading && (
          <div className="mb-4 p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
            <p className="text-red-400 text-sm">{error}</p>
          </div>
        )}

        {/* Grid */}
        {isLoading ? (
          <div className="flex-1 flex items-center justify-center py-16">
            <div className="flex flex-col items-center gap-3">
              <div className="w-8 h-8 border-2 border-[#333333] border-t-[#F4CC04] rounded-full animate-spin" />
              <span className="text-[#666666] text-sm">Buscando agentes...</span>
            </div>
          </div>
        ) : agents.length > 0 ? (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {agents.map((agent) => (
                <AgentCard
                  key={agent.id}
                  agent={agent}
                  isActive={agent.id === activeAgentId}
                  onSelect={handleSelect}
                  onTogglePin={handleTogglePin}
                  onInfo={setDetailAgent}
                  variant="catalog"
                />
              ))}
            </div>
            {metadata?.has_more && (
              <button
                onClick={() => fetchAgents(currentSkip + PAGE_SIZE, true)}
                disabled={isLoadingMore}
                className="
                  mx-auto mt-6 flex items-center gap-2 py-2.5 px-6 text-sm text-[#F4CC04] font-medium
                  hover:bg-[#1a1a1a] rounded-lg transition-colors
                  disabled:opacity-50 disabled:cursor-not-allowed
                "
              >
                {isLoadingMore && <Loader2 className="w-4 h-4 animate-spin" />}
                {isLoadingMore ? 'Carregando...' : 'Carregar mais'}
              </button>
            )}
          </>
        ) : (
          <div className="text-center py-16 text-[#666666]">
            <p>Nenhum agente encontrado{debouncedQuery ? ` para "${debouncedQuery}"` : ''}.</p>
            {hasFilters && (
              <button onClick={handleClearFilters} className="text-[#F4CC04] text-sm mt-2 hover:underline">
                Limpar filtros
              </button>
            )}
          </div>
        )}
      </div>

      {/* Modal de detalhes */}
      {detailAgent && (
        <AgentDetailModal
          agent={detailAgent}
          onSelect={(agent) => {
            setDetailAgent(null);
            handleSelect(agent);
          }}
          onClose={() => setDetailAgent(null)}
        />
      )}
    </div>
  );
};
